import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserData } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  const processAuth = useCallback(async () => {
    try {
      // Extract session_id from URL fragment
      const hash = location.hash || window.location.hash;
      
      const params = new URLSearchParams(hash.replace('#', ''));
      const sessionId = params.get('session_id');

      if (!sessionId) {
        setError('No session ID found. Please try signing in again.');
        setTimeout(() => navigate('/', { replace: true }), 3000);
        return;
      }

      // Exchange session_id for session_token
      const response = await fetch(`${API_URL}/api/auth/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ session_id: sessionId })
      });

      if (!response.ok) {
        throw new Error(`Failed to exchange session: ${response.status}`);
      }

      const userData = await response.json();
      
      // Store user in sessionStorage (more secure than localStorage)
      sessionStorage.setItem('user', JSON.stringify(userData));
      
      setUserData(userData);

      // Clear the hash from URL
      window.history.replaceState(null, '', window.location.pathname);

      // Small delay to ensure state is set before redirect
      await new Promise(resolve => setTimeout(resolve, 300));

      // Navigate to my-enquiries
      window.location.href = '/my-enquiries';

    } catch (err) {
      setError('Sign in failed. Please try again.');
      setTimeout(() => navigate('/', { replace: true }), 3000);
    }
  }, [location.hash, navigate, setUserData]);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    processAuth();
  }, [processAuth]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8 bg-white rounded-2xl shadow-lg max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <p className="text-red-600 font-medium mb-2">{error}</p>
          <p className="text-gray-500 text-sm">Redirecting to homepage...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-sky-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Signing you in...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
